import { Select, SelectType } from "@appquality/appquality-design-system";
import {
  ArrowRepeat,
  Bookmark,
  Bug as BugIcon,
  InfoCircle,
  StarFill,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { MetadataGrid } from "../styled";
import ShowItem from "./ShowItem";
import { BugData, BugEditValues } from "./types";

interface Props {
  data: BugData;
  isEditing: boolean;
  editedValues: BugEditValues;
  availableSeverities: SelectType.Option[];
  availableTypes: SelectType.Option[];
  availableReplicabilities: SelectType.Option[];
  availableUsecases: SelectType.Option[];
  onChange: <K extends keyof BugEditValues>(
    key: K,
    value: BugEditValues[K],
  ) => void;
}

const findOption = (
  options: SelectType.Option[],
  id?: number | string,
): SelectType.Option | undefined =>
  id !== undefined ? options.find((o) => o.value === id.toString()) : undefined;

const enrichOptions = (
  options: SelectType.Option[],
  edited: { id: number; name?: string } | undefined,
  original: { id: number; name?: string } | undefined,
): SelectType.Option[] => {
  const fallback = edited ?? original;
  if (!fallback) return options;
  if (options.some((o) => o.value === fallback.id.toString())) return options;
  return [
    ...options,
    {
      label: fallback.name ?? fallback.id.toString(),
      value: fallback.id.toString(),
      isDisabled: true,
    },
  ];
};

const BugMetadata = ({
  data,
  isEditing,
  editedValues,
  availableSeverities,
  availableTypes,
  availableReplicabilities,
  availableUsecases,
  onChange,
}: Props) => {
  const { t } = useTranslation();

  const usecaseOriginal = data.usecase
    ? { id: data.usecase.id, name: data.usecase.title }
    : undefined;

  const severityOptions = enrichOptions(
    availableSeverities,
    editedValues.severity,
    data.severity,
  );
  const replicabilityOptions = enrichOptions(
    availableReplicabilities,
    editedValues.replicability,
    data.replicability,
  );
  const typeOptions = enrichOptions(
    availableTypes,
    editedValues.type,
    data.type,
  );
  const usecaseOptions = enrichOptions(
    availableUsecases,
    editedValues.usecase,
    usecaseOriginal,
  );

  return (
    <MetadataGrid>
      <ShowItem icon={StarFill} label={t("Severity")}>
        {isEditing ? (
          <Select
            name="severity"
            label=""
            options={severityOptions}
            value={
              findOption(
                severityOptions,
                editedValues.severity?.id ?? data.severity.id,
              ) as SelectType.Option
            }
            onChange={(val: SelectType.Option | null) => {
              if (val && val.value !== undefined) {
                onChange("severity", {
                  id: parseInt(val.value as string),
                  name: String(val.label),
                });
              }
            }}
            isSearchable={false}
          />
        ) : (
          data.severity.name
        )}
      </ShowItem>

      <ShowItem icon={ArrowRepeat} label={t("Replicability")}>
        {isEditing ? (
          <Select
            name="replicability"
            label=""
            options={replicabilityOptions}
            value={
              findOption(
                replicabilityOptions,
                editedValues.replicability?.id ?? data.replicability.id,
              ) as SelectType.Option
            }
            onChange={(val: SelectType.Option | null) => {
              if (val && val.value !== undefined) {
                onChange("replicability", {
                  id: parseInt(val.value as string),
                  name: String(val.label),
                });
              }
            }}
            isSearchable={false}
          />
        ) : (
          data.replicability.name
        )}
      </ShowItem>

      <ShowItem icon={BugIcon} label={t("Type")}>
        {isEditing ? (
          <Select
            name="type"
            label=""
            options={typeOptions}
            value={
              findOption(
                typeOptions,
                editedValues.type?.id ?? data.type.id,
              ) as SelectType.Option
            }
            onChange={(val: SelectType.Option | null) => {
              if (val && val.value !== undefined) {
                onChange("type", {
                  id: parseInt(val.value as string),
                  name: String(val.label),
                });
              }
            }}
            isSearchable={false}
          />
        ) : (
          data.type.name
        )}
      </ShowItem>

      <ShowItem icon={Bookmark} label={t("Use case")}>
        {isEditing ? (
          <Select
            name="usecase"
            label=""
            options={usecaseOptions}
            value={
              findOption(
                usecaseOptions,
                editedValues.usecase?.id ?? data.usecase?.id,
              ) as SelectType.Option
            }
            onChange={(val: SelectType.Option | null) => {
              if (val && val.value !== undefined) {
                onChange("usecase", {
                  id: parseInt(val.value as string),
                  name: String(val.label),
                  description: "",
                });
              }
            }}
            isSearchable={false}
          />
        ) : (
          data.usecase?.title || "—"
        )}
      </ShowItem>
      {data.reason && (
        <ShowItem icon={InfoCircle} label={t("Status reason (reviewer notes)")}>
          {data.reason}
        </ShowItem>
      )}
    </MetadataGrid>
  );
};

export default BugMetadata;
